import { Alert, Box, Typography } from "@mui/material";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  MenuItemRequest,
  ResponseOfMenuItem,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import MenuItemForm from "./MenuItemForm";

const MenuItemUpdateForm = () => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const { menuService, menuItemsUrl, menuItemDetailsUrl } = useGlobalContext();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const menuItemResult = useQuery<ResponseOfMenuItem>(
    ["menuItem", id],
    () => menuService.getMenuItemById(id!),
    {
      enabled: !!id,
    }
  );

  const menuItem = menuItemResult.data?.data;
  useTitle(menuItem?.name || t({ id: "menuItemFormEditButton" }));

  const redirectToDetails = () => {
    navigate(menuItemDetailsUrl(id));
  };

  const onSubmit = async (values: MenuItemRequest) => {
    try {
      values.id = id;
      var result = await registerPromise(menuService.saveMenuItem(values));
      queryClient.invalidateQueries("menuItems");
      queryClient.invalidateQueries(["menuItem", id]);
      redirectToDetails();
      return result;
    } catch (err: any) {
      console.log(err);
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  if (!id) return <Navigate to={menuItemsUrl()} replace />;
  if (menuItemResult.isLoading) return <Loader />;
  if (menuItemResult.isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="menuItemLoadError" />
        </Alert>
      </Box>
    );
  }
  return (
    <>
      <RedirectBackButton
        redirectRoute={menuItemsUrl()}
        labelKey="backToListButtonLabel"
        style={{ marginRight: "10px" }}
      />
      <Box my={2}>
        <Typography variant="h5">
          <T id="menuItemFormEditTitle" />
        </Typography>
      </Box>
      <MenuItemForm
        entity={menuItem}
        onSubmit={onSubmit}
        submitLabel={t({ id: "menuItemFormEditButton" })}
        onCancel={redirectToDetails}
      />
    </>
  );
};

export default MenuItemUpdateForm;
